import { forwardRef, HTMLAttributes, MouseEvent, KeyboardEvent, PointerEvent } from 'react';
import { classNames } from 'primereact/utils';
import { ThumbnailPageInsertPosition } from '../types';
import { BundleType, type BundleTypeKeys, selectViewportPageIndexInStore, useAppSelector } from '@/store';
import { CheckIcon, EyeCircleIcon, QuoteMarksIcon, StarIcon } from '@repo/atomic-components';

export interface ContextMenuShowProps {
  event: MouseEvent<HTMLElement>;
  pageId: string;
}

export interface ThumbnailKeyDownProps {
  event: KeyboardEvent<HTMLElement>;
  pageIndex: number;
  pageId: string;
}

export interface ThumbnailItemClickProps {
  event?: MouseEvent<HTMLElement>;
  pageIndex: number;
  pageId?: string;
}

export interface ThumbnailPointerDownProps {
  event: MouseEvent<HTMLElement>;
  pageIndex: number;
  pageId: string;
}

// prettier-ignore
export interface ThumbnailPageProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'id'> {
  pageId: string;
  pageIndex: number;
  activePageIndex?: number;
  insertPosition?: ThumbnailPageInsertPosition;
  /** Number of pages that will land at the insertion marker; drives the count badge. */
  dropIndicatorCount?: number;
  activeIndex?: number;
  thumbnailUri: string;
  documentName: string;
  pageWidth: number;
  pageHeight: number;
  isDragging?: boolean;
  isSelected?: boolean;
  isCutOut?: boolean;
  isViewed?: boolean;
  isDragItemInSelectedList?: boolean;
  isHighRelevance?: boolean;
  isInLightBundle?: boolean;
  isInChronology?: boolean;
  activeBundleType?: BundleTypeKeys | null;
  clone?: boolean;
  customFooterText?: string;
  onThumbnailItemClick?: (props: ThumbnailItemClickProps) => void;
  onThumbnailPointerDown?: (props: ThumbnailPointerDownProps) => void;
  onContextMenuShow?: (props: ContextMenuShowProps) => void;
  onThumbnailKeyDown?: (props: ThumbnailKeyDownProps) => void;
}

export const ThumbnailPage = forwardRef<HTMLDivElement, ThumbnailPageProps>(
  (
    {
      pageId,
      pageIndex,
      activePageIndex = -1,
      insertPosition,
      dropIndicatorCount = 0,
      thumbnailUri,
      documentName,
      pageWidth,
      pageHeight,
      isSelected = false,
      isCutOut = false,
      isViewed = false,
      isDragItemInSelectedList = false,
      isHighRelevance = false,
      isInLightBundle = false,
      isInChronology = false,
      activeBundleType,
      isDragging = false,
      style,
      clone,
      customFooterText,
      onThumbnailKeyDown,
      onPointerDown,
      onKeyDown,
      onThumbnailItemClick,
      onThumbnailPointerDown,
      onContextMenuShow,
      ...props
    },
    ref,
  ) => {
    // Subscribe to a boolean rather than the raw viewport page index so that
    // scroll-driven viewport updates only re-render the two thumbnails whose
    // active state actually flips (previous active → false, new active → true)
    // instead of every visible thumbnail.
    const isActive = useAppSelector((state) => selectViewportPageIndexInStore(state) === pageIndex);

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
      if (onThumbnailItemClick) {
        onThumbnailItemClick({
          event: event,
          pageIndex: pageIndex,
          pageId: pageId,
        });
      }
    };

    const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
      // Skip right mouse button. 0 — left, 1 — middle, 2 — right
      if (event.button === 2) {
        return;
      }
      if (onPointerDown) {
        onPointerDown(event);
      }
      if (onThumbnailPointerDown) {
        onThumbnailPointerDown({
          event: event,
          pageIndex: pageIndex,
          pageId: pageId,
        });
      }
    };

    const handleContextMenu = (event: MouseEvent<HTMLElement>) => {
      if (onContextMenuShow) {
        onContextMenuShow({ event: event, pageId: pageId });
      }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
      if (onThumbnailKeyDown) {
        onThumbnailKeyDown({
          event: event,
          pageIndex: pageIndex,
          pageId: pageId,
        });
      }
      if (onKeyDown) {
        onKeyDown(event as unknown as KeyboardEvent<HTMLDivElement>);
      }
    };

    const isDraggingInProgress = isDragging || (isSelected && isDragItemInSelectedList && activePageIndex >= 0);

    // The green light-bundle tick only makes sense on the Full Bundle — on the
    // Light Bundle every page is in the light bundle, so there are no ticks.
    const shouldShowLightBundleVisual = activeBundleType === BundleType.FULL && isInLightBundle;
    // The chronology indicator shows on both bundles and takes priority over the
    // light-bundle tick when a page is both extracted and in the chronology.
    const shouldShowChronologyIndicator =
      isInChronology && (activeBundleType === BundleType.FULL || activeBundleType === BundleType.LIGHT);
    const shouldShowHighRelevanceIndicator =
      isHighRelevance && !shouldShowLightBundleVisual && !shouldShowChronologyIndicator;
    const shouldShowCornerIndicator =
      shouldShowHighRelevanceIndicator || shouldShowLightBundleVisual || shouldShowChronologyIndicator;

    // Bold insertion line shown in the column gap next to the page the pointer is
    // anchored on, so a multi-page drag reads as "these land here" rather than a
    // faint sliver. Suppressed on the dragged pages and the drag overlay clone.
    const showDropMarker = !isDraggingInProgress && !clone && !!insertPosition;

    return (
      <>
        <div
          ref={ref}
          className={classNames(
            'relative h-full w-full',
            'flex flex-col justify-end',
            'rounded-[8px]',
            'px-2 pt-2',
            {
              'bg-fletchers-green-50': isActive,
            },
            'cursor-pointer',
            isDraggingInProgress && 'bg-gray-200',
            isDraggingInProgress && 'opacity-30',
            isDraggingInProgress && 'cursor-grab',
            clone && 'shadow-[0_0_0_1px_rgba(63,63,68,0.05),0_1px_6px_0_rgba(34,33,81,0.3)]',
            isSelected && 'border-fletchers-green-300 border-2',
            isCutOut && 'opacity-50 grayscale',
          )}
          style={{
            ...style,
            ...(clone ? { transform: 'translate3d(10px, 10px, 0) scale(1.025)' } : {}),
          }}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onContextMenu={handleContextMenu}
          tabIndex={0}
          data-grid-keyboard-navigation-item-id={pageId}
          onKeyDown={handleKeyDown}
          {...props}
        >
          <div
            // prettier-ignore
            className={classNames(
              'relative block w-full',
              'rounded-sm bg-white outline-none',
              'border-fletchers-gray-200 border',
              // !isDraggingInProgress && 'focus-visible:shadow-[0_0_0_2px_#4c9ffe]',
              clone && 'animate-fletchers-pop',
              {
                'border-fletchers-gray-200 border':
                  !isHighRelevance && !shouldShowLightBundleVisual && !shouldShowChronologyIndicator,
                'relative border-2': isHighRelevance || shouldShowLightBundleVisual || shouldShowChronologyIndicator,
                'border-fletchers-yellow-500':
                  isHighRelevance && !shouldShowLightBundleVisual && !shouldShowChronologyIndicator,
                'border-fletchers-green-500': shouldShowLightBundleVisual && !shouldShowChronologyIndicator,
                // Chronology pages take the teal chronology colour on the border too.
                'border-fletchers-teal-500': shouldShowChronologyIndicator,
              }
            )}
          >
            {showDropMarker && (
              <div
                aria-hidden="true"
                className={classNames(
                  'bg-fletchers-red-500 pointer-events-none absolute top-0 bottom-0 z-30 w-[6px] rounded-full',
                  'shadow-[0_0_0_2px_rgba(255,255,255,0.7)]',
                  insertPosition === ThumbnailPageInsertPosition.Before ? '-left-[20px]' : '-right-[20px]',
                )}
              >
                {dropIndicatorCount > 1 && (
                  <span className="bg-fletchers-red-500 text-fletchers-white absolute -top-[10px] left-1/2 -translate-x-1/2 rounded-full px-[6px] py-px text-[10px] leading-[14px] font-semibold shadow-sm">
                    {dropIndicatorCount}
                  </span>
                )}
              </div>
            )}
            <div
              className={classNames(
                'absolute inset-0 z-10 rounded-sm',
                'bg-[linear-gradient(0deg,rgba(150,150,150,0.4),rgba(150,150,150,0.4))]',
                { hidden: !isViewed },
              )}
            />
            {isViewed && (
              <EyeCircleIcon
                className="absolute top-[4px] left-[4px] z-20 h-[20px] w-[20px]"
                width="20"
                height={undefined}
              />
            )}
            {shouldShowCornerIndicator && (
              // Corner badge, sized as a square in the top-right. Contains a
              // full-size inline SVG that draws a "quarter-pie slice" — two
              // flat outer edges (top + right, exactly flush with the
              // thumbnail corner — pixel-aligned, no sub-pixel gap possible)
              // and a quarter-circle arc on the inner (bottom-left) corner.
              // Icons render as a sibling on top, pushed toward the outer
              // corner so they sit in the visual centre of the pie slice.
              <div className="pointer-events-none absolute top-0 right-0 z-20 aspect-square max-h-6 w-[30%] max-w-6">
                <svg
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  className={classNames('h-full w-full', {
                    'fill-fletchers-yellow-500': shouldShowHighRelevanceIndicator,
                    'fill-fletchers-green-500': shouldShowLightBundleVisual && !shouldShowChronologyIndicator,
                    'fill-fletchers-teal-500': shouldShowChronologyIndicator,
                  })}
                  aria-hidden="true"
                >
                  <path d="M 0 0 L 100 0 L 100 100 A 100 100 0 0 1 0 0 Z" />
                </svg>
                <div className="absolute inset-0 flex translate-x-[12%] -translate-y-[12%] items-center justify-center">
                  {shouldShowChronologyIndicator ? (
                    <QuoteMarksIcon className="w-[55%] fill-white stroke-white" aria-hidden="true" />
                  ) : shouldShowLightBundleVisual ? (
                    <CheckIcon className="fill-white" width="50%" height={undefined} />
                  ) : (
                    <StarIcon className="fill-white" width="50%" height={undefined} />
                  )}
                </div>
              </div>
            )}
            <img
              className="block h-auto w-full rounded-[3px] border-none bg-white outline-none"
              data-id={pageId}
              src={thumbnailUri}
              alt={`Thumbnail for ${documentName}`}
              data-testid={pageId}
              width={pageWidth}
              height={pageHeight}
              decoding="async"
            />
          </div>

          <div className="animate-fletchers-fadeIn mt-2 inline-block w-full text-center text-[10px]/[20px] select-none">
            {customFooterText ? customFooterText : pageIndex + 1}
          </div>
        </div>
      </>
    );
  },
);
